import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useLangNavigate from "../../hooks/useLangNavigate";
import { usePageMeta } from "../../hooks/useMeta";
import {
  LandingHeader,
  LandingFooter,
  ContactFounder,
  LandingCarousel,
  handleImageError,
} from "../../components/landing";
import Button from "../../components/buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faSmile,
  faFrown,
  faUsers,
  faMoneyBill,
  faKey,
  faCheck,
  faTimes,
  faUtensils,
  faClock,
  faHandshake,
  faPhone,
  faEnvelope,
  faGavel,
} from "@fortawesome/pro-solid-svg-icons";
import { faChevronDown, faArrowRight } from "@fortawesome/pro-light-svg-icons";

// Images
import companyValueImg from "../../assets/img/landing/companyvalue.webp";
import companyBreakImg from "../../assets/img/landing/company-break.webp";
import benefitsBgImg from "../../assets/img/landing/reworked/resto.webp";
import restoImg from "../../assets/img/landing/reworked/resto.webp";
import founderIlirImg from "../../assets/img/landing/reworked/about/founder-1.webp";
import companyCodeImg from "../../assets/img/landing/reworked/company_code.webp";
import companyOverallImg from "../../assets/img/landing/reworked/company_overall.webp";
import companyPartnersImg from "../../assets/img/landing/reworked/company_partners.webp";

function LandingCompanies() {
  const { t } = useTranslation();
  const navigate = useLangNavigate();

  // Parallax scroll effect
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Floating CTA visibility
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const heroRef = useRef(null);
  const finalCtaRef = useRef(null);

  // FAQ accordion state - first item open by default
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Hero carousel (dashboard screens from reworked assets)
  const carouselImages = useMemo(
    () => [
      {
        src: companyOverallImg,
        alt: t("landing.companies.carousel.overall", "Company overall"),
        description: t("landing.companies.carousel.overall", "Company overall"),
      },
      {
        src: companyCodeImg,
        alt: t("landing.companies.carousel.code", "Company code"),
        description: t("landing.companies.carousel.code", "Company code"),
      },
      {
        src: companyPartnersImg,
        alt: t("landing.companies.carousel.partners", "Company partners"),
        description: t(
          "landing.companies.carousel.partners",
          "Company partners",
        ),
      },
    ],
    [t],
  );
  useEffect(() => {
    const heroHeight = heroRef.current?.offsetHeight || 600;
    const finalCtaTop = finalCtaRef.current?.offsetTop || Infinity;
    const windowHeight = window.innerHeight;

    const showThreshold = heroHeight * 0.8;
    const hideThreshold = finalCtaTop - windowHeight + 200;

    setShowFloatingCta(scrollY > showThreshold && scrollY < hideThreshold);
  }, [scrollY]);

  // Stats data
  const stats = useMemo(
    () => [
      {
        value: t("landing.companies.stats.price.value"),
        label: t("landing.companies.stats.price.label"),
      },
      {
        value: t("landing.companies.stats.register.value"),
        label: t("landing.companies.stats.register.label"),
      },
      {
        value: t("landing.companies.stats.local.value"),
        label: t("landing.companies.stats.local.label"),
      },
    ],
    [t],
  );

  // Steps data
  const steps = useMemo(
    () => [
      {
        number: 1,
        title: t("landing.companies.steps.step1.title"),
        description: t("landing.companies.steps.step1.description"),
        image: companyValueImg,
      },
      {
        number: 2,
        title: t("landing.companies.steps.step2.title"),
        description: t("landing.companies.steps.step2.description"),
        image: companyBreakImg,
      },
      {
        number: 3,
        title: t("landing.companies.steps.step3.title"),
        description: t("landing.companies.steps.step3.description"),
        image: restoImg,
      },
    ],
    [t],
  );

  // Features data
  const features = useMemo(
    () => [
      {
        icon: faUsers,
        title: t("landing.companies.features.management.title"),
        description: t("landing.companies.features.management.description"),
      },
      {
        icon: faMoneyBill,
        title: t("landing.companies.features.costs.title"),
        description: t("landing.companies.features.costs.description"),
      },
      {
        icon: faKey,
        title: t("landing.companies.features.controls.title"),
        description: t("landing.companies.features.controls.description"),
      },
    ],
    [t],
  );

  // FAQ items
  const faqs = useMemo(
    () => [
      {
        question: t("landing.companies.faq.items.cost.question"),
        answer: t("landing.companies.faq.items.cost.answer"),
      },
      {
        question: t("landing.companies.faq.items.restaurants.question"),
        answer: t("landing.companies.faq.items.restaurants.answer"),
      },
      {
        question: t("landing.companies.faq.items.setup.question"),
        answer: t("landing.companies.faq.items.setup.answer"),
      },
      {
        question: t("landing.companies.faq.items.employees.question"),
        answer: t("landing.companies.faq.items.employees.answer"),
      },
    ],
    [t],
  );

  return (
    <>
      {usePageMeta("landingCompanies")}
      <div className="landing-company">
        {/* Parallax gradient accents */}
        <div
          className="landing-company__gradient-1"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        />
        <div
          className="landing-company__gradient-2"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />

        {/* Skip to main content for accessibility */}
        <a href="#main-content" className="landing-company__skip-link">
          {t("common.skipToContent", "Skip to main content")}
        </a>

        <LandingHeader />

        {/* Hero Section - Dark theme with purple accents */}
        <section
          className="landing-hero-section landing-company__hero"
          ref={heroRef}
          id="main-content"
        >
          <div className="landing-hero-dots">
            {Array.from({ length: 100 }).map((_, i) => (
              <div
                key={i}
                className={`landing-hero-dot landing-hero-dot--${i % 8}`}
                style={{
                  left: `${5 + ((i * 11) % 90)}%`,
                  top: `${8 + ((i * 13) % 85)}%`,
                  animationDelay: `${i * 0.15}s`,
                }}
                aria-hidden="true"
              />
            ))}
          </div>
          <div className="landing-company__hero-content">
            <div className="landing-company__hero-text">
              <span className="landing-company__tagline">
                {t("landing.companies.hero.badge")}
              </span>
              <h1 className="landing-company__headline">
                {t("landing.companies.hero.headline")}
              </h1>
              <p className="landing-company__subheadline">
                {t("landing.companies.hero.subheadline")}
              </p>

              {/* CTA Buttons */}
              <div className="landing-company__cta-buttons">
                <Button
                  variant="primary"
                  size="md"
                  className="landing-cta-button--glow"
                  onClick={() => navigate("register")}
                  data-analytics="cta-company-register"
                  data-analytics-location="hero"
                >
                  {t("landing.companies.cta.primary")}
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </Button>
                <Button
                  variant="white"
                  size="md"
                  onClick={() => navigate("support")}
                  data-analytics="cta-company-contact"
                  data-analytics-location="hero"
                >
                  {t("landing.companies.cta.secondary")}
                </Button>
              </div>

              {/* Trust items */}
              <div className="landing-company__trust-items">
                <div className="landing-company__trust-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{t("landing.companies.hero.trust1")}</span>
                </div>
                <div className="landing-company__trust-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{t("landing.companies.hero.trust2")}</span>
                </div>
              </div>
            </div>

            {/* Hero Visual - Carousel of dashboard screens */}
            <div className="landing-company__hero-visual">
              <div
                className="landing-floating-accent landing-company__floating-accent"
                aria-hidden="true"
              />
              <LandingCarousel images={carouselImages} />
            </div>
          </div>

          {/* Stats integrated in hero */}
          <div className="landing-company__stats">
            {stats.map((stat, index) => (
              <div key={index} className="landing-company__stat">
                <span className="landing-company__stat-value">
                  {stat.value}
                </span>
                <span className="landing-company__stat-label">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* How it Works Section - With Images */}
        <section className="landing-section landing-company__preorder-flow">
          <div className="landing-company__section-container">
            <h2 className="landing-section-title landing-company__section-title">
              {t("landing.companies.steps.title")}
            </h2>
            <p className="landing-section-subtitle landing-company__section-subtitle">
              {t("landing.companies.steps.subtitle")}
            </p>

            <div className="landing-company__steps-grid">
              {steps.map((step) => (
                <div key={step.number} className="landing-company__step-row">
                  <div className="landing-step-card landing-company__step-card">
                    <div className="landing-step-number landing-company__step-number">
                      {step.number}
                    </div>
                    <div className="landing-step-card__content landing-company__step-content">
                      <h3 className="landing-step-card__title landing-company__step-title">
                        {step.title}
                      </h3>
                      <p
                        dangerouslySetInnerHTML={{ __html: step.description }}
                        className="landing-step-card__description landing-company__step-description"
                      />
                    </div>
                  </div>
                  <div className="landing-step-image landing-company__step-image">
                    <img
                      src={step.image}
                      alt={step.title}
                      loading="lazy"
                      onError={handleImageError}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section - Comparison */}
        <section
          className="landing-section landing-company__benefits"
          style={{ "--benefits-bg-image": `url(${benefitsBgImg})` }}
        >
          <div className="landing-company__section-container">
            <h2 className="landing-company__benefits-title">
              {t("landing.companies.benefits.sectionTitle")}
            </h2>

            <div className="landing-company__comparison">
              {/* Without OFFOOD */}
              <div className="landing-comparison-card landing-comparison-card--without">
                <div className="landing-comparison-card__header">
                  <span className="landing-comparison-card__icon landing-comparison-card__icon--negative">
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                  <h3 className="landing-comparison-card__title">
                    {t("landing.companies.benefits.comparison.without.title")}
                  </h3>
                </div>
                <ul className="landing-comparison-card__list">
                  <li>
                    <FontAwesomeIcon icon={faClock} />
                    <span>
                      {t("landing.companies.benefits.comparison.without.step1")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCog} />
                    <span>
                      {t("landing.companies.benefits.comparison.without.step2")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faFrown} />
                    <span>
                      {t("landing.companies.benefits.comparison.without.step3")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faGavel} />
                    <span>
                      {t("landing.companies.benefits.comparison.without.step4")}
                    </span>
                  </li>
                </ul>
                <div className="landing-comparison-card__footer landing-comparison-card__footer--negative">
                  {t("landing.companies.benefits.comparison.without.result")}
                </div>
              </div>

              {/* With OFFOOD */}
              <div className="landing-comparison-card landing-comparison-card--with">
                <div className="landing-comparison-card__header">
                  <span className="landing-comparison-card__icon landing-comparison-card__icon--positive">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <h3 className="landing-comparison-card__title">
                    {t("landing.companies.benefits.comparison.with.title")}
                  </h3>
                </div>
                <ul className="landing-comparison-card__list">
                  <li>
                    <FontAwesomeIcon icon={faHandshake} />
                    <span>
                      {t("landing.companies.benefits.comparison.with.step1")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faUtensils} />
                    <span>
                      {t("landing.companies.benefits.comparison.with.step2")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faSmile} />
                    <span>
                      {t("landing.companies.benefits.comparison.with.step3")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>
                      {t("landing.companies.benefits.comparison.with.step4")}
                    </span>
                  </li>
                </ul>
                <div className="landing-comparison-card__footer landing-comparison-card__footer--positive">
                  {t("landing.companies.benefits.comparison.with.result")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="landing-section landing-company__features">
          <div className="landing-company__section-container">
            <h2 className="landing-section-title landing-company__section-title">
              {t("landing.companies.features.title")}
            </h2>
            <p className="landing-section-subtitle landing-company__section-subtitle">
              {t("landing.companies.features.subtitle")}
            </p>

            <div className="landing-company__features-grid">
              {features.map((feature, index) => (
                <div key={index} className="landing-company__feature-card">
                  <div className="landing-company__feature-icon">
                    <FontAwesomeIcon icon={feature.icon} />
                  </div>
                  <div className="landing-company__feature-content">
                    <h4 className="landing-company__feature-title">
                      {feature.title}
                    </h4>
                    <p className="landing-company__feature-description">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section - shared .landing-faq* only (_landing-shared.scss) */}
        <section className="landing-section landing-faq landing-company__faq">
          <div className="landing-company__section-container">
            <h2 className="landing-section-title landing-company__section-title">
              {t("landing.companies.faq.title")}
            </h2>

            <div className="landing-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`landing-faq-item${openFaqIndex === index ? " landing-faq-item--open" : ""}`}
                >
                  <button
                    type="button"
                    className="landing-faq-question"
                    onClick={() =>
                      setOpenFaqIndex(openFaqIndex === index ? null : index)
                    }
                    aria-expanded={openFaqIndex === index}
                  >
                    <span>{faq.question}</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="landing-faq-icon"
                    />
                  </button>
                  <div className="landing-faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Ilir - before final CTA */}
        <ContactFounder
          sectionTitle={t("landing.contactIlir.sectionTitle")}
          image={founderIlirImg}
          imageAlt={t("landing.contactIlir.name")}
          name={t("landing.contactIlir.name")}
          role={t("landing.contactIlir.role")}
          paragraph={t("landing.contactIlir.paragraph")}
          primaryCta={{
            url: "https://calendly.com/ilir-offood/30min",
            label: t("landing.contactIlir.bookCall"),
            icon: faPhone,
          }}
          secondaryCta={{
            url: "mailto:ilir@offood.ch",
            label: t("landing.contactIlir.emailMe"),
            icon: faEnvelope,
          }}
        />

        {/* Final CTA Section */}
        <section
          className="landing-section landing-company__final-cta"
          ref={finalCtaRef}
        >
          <div className="landing-company__final-cta-container">
            <h2 className="landing-company__cta-headline">
              {t("landing.companies.finalCta.headline")}
            </h2>
            <p className="landing-company__cta-subheadline">
              {t("landing.companies.finalCta.subheadline")}
            </p>
            <div className="landing-company__cta-buttons-final">
              <Button
                variant="white"
                size="md"
                className="landing-cta-button--glow"
                onClick={() => navigate("register")}
                data-analytics="cta-company-register"
                data-analytics-location="footer-cta"
              >
                {t("landing.companies.cta.primary")}
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
              <Button
                variant="primary"
                size="md"
                outlined
                onClick={() => navigate("support")}
                data-analytics="cta-company-contact"
                data-analytics-location="footer-cta"
              >
                {t("landing.companies.cta.secondary")}
              </Button>
            </div>

            {/* Swiss trust signal */}
            <div className="landing-company__swiss-trust">
              <svg
                width="20"
                height="20"
                viewBox="0 0 32 32"
                aria-label="Switzerland"
                role="img"
              >
                <rect width="32" height="32" fill="#D52B1E" rx="2" />
                <path d="M13 6h6v7h7v6h-7v7h-6v-7H6v-6h7V6z" fill="#FFF" />
              </svg>
              <span>{t("landing.entry.trust.swissLabel")}</span>
            </div>
          </div>
        </section>

        {/* Banderole CTA */}
        {showFloatingCta && (
          <div className="landing-banderole">
            <p className="landing-banderole__text">
              {t("landing.companies.banderole.text")}
            </p>
            <div className="landing-banderole__cta">
              <Button
                variant="white"
                size="md"
                onClick={() => navigate("register")}
                data-analytics="cta-company-register"
                data-analytics-location="banderole"
              >
                {t("landing.companies.cta.primary")}
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        <LandingFooter />
      </div>
    </>
  );
}

export default LandingCompanies;
